import React, { FC, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { DeviceCommandDto, ErrorResponse } from '@thingslog/repositories';
import { commandsQueryClient } from '../../../../clients/ReactQueryClients/ReactQueryClients';
import { translateCommandType } from '../utils/commandLabels';

/**
 * `sx`, not Tailwind: a Dialog portals to `document.body`, and the preset emits
 * every utility as `#root .foo`, which cannot match inside a portal.
 */
const CancelCommandDialog: FC<CancelCommandDialogProps> = ({
  command,
  deviceNumber,
  onClose,
  onCancelled,
}: CancelCommandDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { useUpdateCommand } = commandsQueryClient;

  const updateCommand = useUpdateCommand({
    onSuccess: (): void => {
      onCancelled();
      onClose();
    },
    onError: (axiosError: AxiosError<ErrorResponse>): void => {
      const message =
        axiosError.response?.data?.errorCode === 'COMMAND_STATE_IMMUTABLE'
          ? t('commands_only_pending_can_be_cancelled')
          : axiosError.response?.data?.message ?? t('commands_error_cancel_failed');
      setError(message);
      onCancelled();
    },
  });

  const handleConfirm = (): void => {
    if (!command) return;
    setError(null);
    updateCommand.mutate({
      deviceNumber,
      commandId: command.id,
      body: { commandState: 'CANCELED' },
    });
  };

  return (
    <Dialog
      open={command !== null}
      onClose={updateCommand.isLoading ? undefined : onClose}
      disableEscapeKeyDown={updateCommand.isLoading}
      fullWidth
      maxWidth="xs"
      sx={{ '& .MuiDialogActions-root .MuiButton-root': { minHeight: { xs: 44, sm: 'auto' } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>{t('commands_cancel_command')}</DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        <DialogContentText variant="body2" sx={{ mb: 2 }}>
          {t('commands_cancel_command_confirm')}
        </DialogContentText>

        {command && (
          <Box
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle2">
              {translateCommandType(t, command.commandType)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('commands_command_id')} {command.id}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} role="alert">
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={updateCommand.isLoading} autoFocus>
          {t('commands_btn_keep')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={updateCommand.isLoading || !command}
          startIcon={
            updateCommand.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t('commands_cancel_command')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CancelCommandDialogProps {
  command: DeviceCommandDto | null;
  deviceNumber: string;
  onClose: () => void;
  onCancelled: () => void;
}

export default CancelCommandDialog;
